/* eslint-disable jsx-a11y/no-redundant-roles -- role='list' / 'listitem' are the design's: Safari drops list semantics from a list styled with list-style: none */
import { Trans, useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import { PauseChip } from '../components/pause-chip';
import { Picture } from '../components/picture';
import { CONTAINER_CLASS, EYEBROW_CLASS, GOLD_EM_CLASS, SECTION_CLASS, SECTION_HEAD_CLASS, SECTION_TITLE_CLASS, SectionBackground, SimChip } from '../components/ui';
import { V4A_NAMESPACE } from '../constants';
import { useDocumentVisible, useOnScreen, useReducedMotion } from '../hooks/use-page-motion';

/* ONE simulated state drives both traces, both numerals and the delta chip, so the chip can never
   contradict the pair beside it. The traces live in a band (y 40 to 110): the top and bottom 40 user
   units are the legend plates' lanes, so no stroke reaches their type. */
const WIDTH = 600;
const HEIGHT = 150;
const SAMPLES = 56;
const CEILING = 80;
const TOP = 40;
const FLOOR = 110;
const VALUE_MIN = 30;
const VALUE_MAX = 270;
const KEEP = 1 - CEILING / 100;
const TICK_MS = 520;

interface Profile {
  base: number;
  spread: number;
  min: number;
  lift: number;
  spike: number;
  spikeMax: number;
}

const CURSED: Profile = { base: 208, spread: 17, min: 150, lift: 26, spike: 0.1, spikeMax: 34 };
const ENCHANTED: Profile = { base: 42, spread: 3, min: 41, lift: 0, spike: 0, spikeMax: 0 };

interface Readings {
  bad: number[];
  good: number[];
}

function sample(profile: Profile, random: () => number): number {
  let value = profile.base + (random() - 0.5) * 2 * profile.spread;
  if (profile.spike && random() < profile.spike) value = profile.base + profile.lift + random() * profile.spikeMax;
  return Math.max(profile.min, Math.min(VALUE_MAX, Math.round(value)));
}

/* The good value of a pair is floored at 20% of its bad value: the printed delta, recomputed from the
   pair on screen, is always true and never beats the published 80%. */
function pair(random: () => number): [number, number] {
  const bad = sample(CURSED, random);
  return [bad, Math.max(sample(ENCHANTED, random), Math.ceil(bad * KEEP))];
}

function seed(random: () => number): Readings {
  const readings: Readings = { bad: [], good: [] };
  for (let index = 0; index < SAMPLES; index += 1) {
    const [bad, good] = pair(random);
    readings.bad.push(bad);
    readings.good.push(good);
  }
  return readings;
}

/** Deterministic stand-in for Math.random on the server render, so the markup hydrates as is. */
function seededRandom(start: number): () => number {
  let state = start;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

function yOf(value: number): number {
  const ratio = (Math.min(Math.max(value, VALUE_MIN), VALUE_MAX) - VALUE_MIN) / (VALUE_MAX - VALUE_MIN);
  return +(FLOOR - ratio * (FLOOR - TOP)).toFixed(1);
}

function pathOf(values: readonly number[]): string {
  const step = WIDTH / (SAMPLES - 1);
  return values.map((value, index) => `${index ? '' : 'M'}${+(index * step).toFixed(1)} ${yOf(value)}`).join(' ');
}

const INITIAL = seed(seededRandom(20260929));

const SYMPTOM_BAD_CLASS = 'rounded-[var(--r-sm)] border border-solid border-[color:rgb(var(--curse-rgb)_/_.26)] bg-[color:rgb(var(--curse-rgb)_/_.1)] px-[.42rem] py-[.2rem] text-[length:11px] leading-[1.3] text-[color:var(--curse-dim)]';
const SYMPTOM_GOOD_CLASS = 'rounded-[var(--r-sm)] border border-solid border-[color:rgb(var(--accent-rgb)_/_.3)] bg-[color:rgb(var(--accent-rgb)_/_.1)] px-[.42rem] py-[.2rem] text-[length:11px] leading-[1.3] text-[color:#C9F79A]';
const COLUMN_LABEL_CLASS = 'grimoire__lab m-0 flex min-w-0 flex-wrap items-center gap-[.4rem] font-mono text-[length:var(--fs-2xs)] uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--fg-dim)]';
const VALUE_CLASS = 'tabular whitespace-nowrap text-[length:clamp(1.75rem,_1.1rem_+_2.2vw,_2.5rem)] font-bold tabular-nums leading-none tracking-[-.03em]';
const TRUST_LABEL_CLASS = 'trust__k m-0 font-mono text-[length:var(--fs-2xs)] uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--fg-faint-aa)]';
const PLATFORM_CLASS = 'plat inline-flex items-center gap-[.4rem] text-[length:var(--fs-xs)] text-[color:var(--fg-dim)]';

function PingChart({ readings }: { readings: Readings }) {
  const { t } = useTranslation(V4A_NAMESPACE);
  const bad = pathOf(readings.bad);
  const good = pathOf(readings.good);
  return (
    <div className='grimoire__chart relative min-w-0 overflow-hidden rounded-[var(--r-md)] border border-solid border-[color:var(--rim-2)] lg:grid lg:[grid-area:chart]'>
      <svg className='grimoire__svg' viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio='none' width={WIDTH} height={HEIGHT} aria-hidden='true' focusable='false'>
        <defs>
          <linearGradient id='v4a-curse' x1='0' x2='0' y1='0' y2='1'>
            <stop offset='0' stopColor='#FF4D6D' stopOpacity='.34' />
            <stop offset='1' stopColor='#FF4D6D' stopOpacity='0' />
          </linearGradient>
          <linearGradient id='v4a-spell' x1='0' x2='0' y1='0' y2='1'>
            <stop offset='0' stopColor='#93FF18' stopOpacity='.42' />
            <stop offset='1' stopColor='#93FF18' stopOpacity='0' />
          </linearGradient>
        </defs>
        <g className='grimoire__rules'>
          <path d='M0 40H600' />
          <path d='M0 75H600' />
          <path d='M0 110H600' />
        </g>
        <path className='grimoire__area grimoire__area--bad' d={`${bad} L${WIDTH} ${HEIGHT} L0 ${HEIGHT} Z`} />
        <path className='grimoire__line grimoire__line--bad' d={bad} />
        <path className='grimoire__area grimoire__area--good' d={`${good} L${WIDTH} ${HEIGHT} L0 ${HEIGHT} Z`} />
        <path className='grimoire__line grimoire__line--good' d={good} />
      </svg>
      <span className='grimoire__tag grimoire__tag--bad absolute end-[.55rem] top-2 rounded-[var(--r-sm)] border border-solid border-[color:rgb(var(--curse-rgb)_/_.5)] bg-[color:rgb(var(--curse-rgb)_/_.2)] px-2 py-[.22rem] font-mono text-[length:11px] uppercase leading-[1.2] tracking-[.08em] text-[color:#FFD9E0]'>{t('grimoire.tag_bad')}</span>
      <span className='grimoire__tag grimoire__tag--good absolute bottom-2 end-[.55rem] rounded-[var(--r-sm)] bg-[color:var(--accent)] px-2 py-[.22rem] font-mono text-[length:11px] uppercase leading-[1.2] tracking-[.08em] text-[color:var(--accent-ink)] forced-colors:border forced-colors:border-solid forced-colors:border-[color:CanvasText]'>{t('grimoire.tag_good')}</span>
    </div>
  );
}

function Trust() {
  const { t } = useTranslation(V4A_NAMESPACE);
  return (
    <div className='trust mt-[clamp(1.75rem,_1.25rem_+_1.6vw,_2.75rem)] flex flex-wrap items-center justify-between gap-x-[clamp(1.25rem,_.5rem_+_2vw,_2.5rem)] gap-y-4 border-t border-solid border-t-[color:var(--rim)] pt-5'>
      <div className='trust__grp flex flex-wrap items-center gap-x-4 gap-y-3'>
        <p className={TRUST_LABEL_CLASS}>{t('trust.certified_by')}</p>
        <span className='cert inline-flex items-center gap-[.45rem] text-[length:var(--fs-xs)] text-[color:var(--fg-dim)]'>
          <Picture name='cert-microsoft-96' alt='' width={96} height={99} loading='lazy' className='h-[1.35rem] w-auto' />
          <span>Microsoft</span>
        </span>
        <span className='cert inline-flex items-center gap-[.45rem] text-[length:var(--fs-xs)] text-[color:var(--fg-dim)]'>
          <Picture name='cert-digicert-128' alt='' width={128} height={52} loading='lazy' className='is-digicert h-[1.35rem] w-auto' />
          <span className='sr-only'>DigiCert</span>
        </span>
      </div>
      <div className='trust__grp trust__grp--end flex flex-wrap items-center gap-x-4 gap-y-3'>
        <p className={TRUST_LABEL_CLASS}>{t('trust.available_for')}</p>
        <span className={PLATFORM_CLASS}>
          <svg width='15' height='15' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' focusable='false'>
            <path d='M3 5.4 10.4 4.4v7.1H3zM11.6 4.2 21 3v8.5h-9.4zM3 12.5h7.4v7.1L3 18.6zM11.6 12.5H21V21l-9.4-1.3z' />
          </svg>
          Windows
        </span>
        <span className={PLATFORM_CLASS}>
          <svg width='15' height='15' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' focusable='false'>
            <path d='M16.2 12.6c0-2.2 1.8-3.3 1.9-3.4-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.7.8-3.4.8s-1.8-.8-2.9-.8c-1.5 0-2.9.9-3.7 2.2-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.6 2.3 2.8 2.2 1.1 0 1.5-.7 2.9-.7s1.7.7 2.9.7c1.2 0 2-1.1 2.7-2.2.9-1.2 1.2-2.5 1.3-2.5-.1 0-2.4-1-2.4-3.6M14 5.9c.6-.8 1-1.8.9-2.9-.9 0-2 .6-2.6 1.4-.6.7-1.1 1.7-.9 2.8 1 0 2-.5 2.6-1.3' />
          </svg>
          iOS
        </span>
        <span className={PLATFORM_CLASS}>
          <svg width='15' height='15' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' focusable='false'>
            <path d='M6 9h12v8.2c0 .6-.5 1.1-1.1 1.1H15V21a1.3 1.3 0 0 1-2.6 0v-2.7h-.8V21a1.3 1.3 0 0 1-2.6 0v-2.7H7.1c-.6 0-1.1-.5-1.1-1.1zM4.3 9.2A1.3 1.3 0 0 1 5.6 10.5v4.6a1.3 1.3 0 0 1-2.6 0v-4.6a1.3 1.3 0 0 1 1.3-1.3m15.4 0A1.3 1.3 0 0 1 21 10.5v4.6a1.3 1.3 0 0 1-2.6 0v-4.6a1.3 1.3 0 0 1 1.3-1.3M8.6 3.4l1 1.8A6.4 6.4 0 0 1 12 4.8c.9 0 1.7.1 2.4.4l1-1.8a.3.3 0 1 1 .5.3l-1 1.7A5.3 5.3 0 0 1 17.9 8H6.1a5.3 5.3 0 0 1 3-2.6l-1-1.7a.3.3 0 0 1 .5-.3M9.4 6.2a.6.6 0 1 0 0 1.2.6.6 0 0 0 0-1.2m5.2 0a.6.6 0 1 0 0 1.2.6.6 0 0 0 0-1.2' />
          </svg>
          Android
        </span>
      </div>
    </div>
  );
}

/** "Leitura do grimório": the ping before and after the counter-spell, live while on screen. */
export function Grimoire() {
  const { t } = useTranslation(V4A_NAMESPACE);
  const figureRef = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const pageVisible = useDocumentVisible();
  const onScreen = useOnScreen(figureRef, { rootMargin: '120px 0px' });
  const [readings, setReadings] = useState<Readings>(INITIAL);
  const [paused, setPaused] = useState<boolean | null>(null);
  const isPaused = paused ?? reduced;

  // A fresh random history once on the client (the server render uses the seeded one).
  useEffect(() => {
    setReadings(seed(Math.random));
  }, []);

  useEffect(() => {
    if (isPaused || !onScreen || !pageVisible) return undefined;
    const timer = window.setInterval(() => {
      setReadings((previous) => {
        const [bad, good] = pair(Math.random);
        return { bad: [...previous.bad.slice(1), bad], good: [...previous.good.slice(1), good] };
      });
    }, TICK_MS);
    return () => window.clearInterval(timer);
  }, [isPaused, onScreen, pageVisible]);

  const lastBad = readings.bad[readings.bad.length - 1] ?? CURSED.base;
  const lastGood = readings.good[readings.good.length - 1] ?? ENCHANTED.base;
  const delta = Math.min(CEILING, Math.max(0, Math.round((1 - lastGood / lastBad) * 100)));

  return (
    <section className={`${SECTION_CLASS} sec--probe`} id='grimorio' aria-labelledby='probe-title'>
      <SectionBackground halos={[{ position: 'right', tone: 'violet' }]} />
      <div className={CONTAINER_CLASS}>
        <div className={`${SECTION_HEAD_CLASS} mb-[clamp(1.5rem,_1rem_+_1.8vw,_2.5rem)]`} data-reveal=''>
          <div className='min-w-0'>
            <p className={EYEBROW_CLASS}>{t('grimoire.eyebrow')}</p>
            <h2 className={SECTION_TITLE_CLASS} id='probe-title'>
              <Trans t={t} i18nKey='grimoire.title' components={{ em: <em className={GOLD_EM_CLASS} /> }} />
            </h2>
          </div>
        </div>

        <figure ref={figureRef} className="grimoire relative m-0 grid gap-4 rounded-[var(--r-xl)] border border-solid border-[color:var(--rim)] p-[clamp(.9rem,_.55rem_+_1.4vw,_1.35rem)] [box-shadow:var(--shadow-lg),_inset_0_1px_0_rgb(255_255_255_/_.06)] lg:grid-cols-[minmax(0,_1fr)_minmax(0,_40%)] lg:items-stretch lg:gap-x-[clamp(.85rem,_.3rem_+_1vw,_1.35rem)] lg:[grid-template-areas:'bar_bar'_'chart_cols'_'foot_foot'] forced-colors:border-[color:CanvasText]" data-probe='' data-reveal=''>
          <figcaption className='grimoire__bar flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 lg:[grid-area:bar]'>
            <span className='grimoire__k inline-flex min-w-0 flex-wrap items-center gap-x-[.4rem] gap-y-[.15rem] font-mono text-[length:var(--fs-2xs)] uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--fg-dim)]'>
              <i className='size-[6px] flex-none rounded-[50%] bg-[color:var(--accent)] [box-shadow:var(--glow-sm)]' aria-hidden='true' />
              {t('grimoire.route_from')}
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
                <path d='M5 12h13' />
                <path d='m13 7 5 5-5 5' />
              </svg>
              {t('grimoire.route_to')}
            </span>
            <span className='grimoire__tools inline-flex flex-wrap items-center justify-end gap-2'>
              <SimChip label={t('sim.label')} />
              <PauseChip className='grimoire__pause' paused={isPaused} onToggle={() => setPaused(!isPaused)} labelPause={t('grimoire.pause')} labelResume={t('grimoire.resume')} />
            </span>
          </figcaption>

          <PingChart readings={readings} />

          <div className='grimoire__cols grid min-w-0 grid-cols-[minmax(0,_1fr)] gap-3 min-[400px]:grid-cols-2 lg:[grid-area:cols]'>
            <div className='grimoire__col grimoire__col--bad grid min-w-0 content-start rounded-[var(--r-md)] border border-solid border-[color:rgb(var(--curse-rgb)_/_.28)] bg-[color:rgb(var(--curse-rgb)_/_.06)] p-3 lg:content-between'>
              <p className={COLUMN_LABEL_CLASS}>{t('grimoire.col_bad')}</p>
              <p className='grimoire__val m-0 mt-1 flex min-w-0 items-baseline gap-1 lg:m-0 lg:mt-2'>
                <b className={`${VALUE_CLASS} text-[color:var(--curse)]`} data-probe-off=''>
                  {lastBad}
                </b>
                <em className='text-[length:var(--fs-xs)] not-italic text-[color:var(--fg-dim)]'>ms</em>
              </p>
              <ul className='grimoire__sym m-0 mt-[.55rem] flex min-w-0 list-none flex-wrap gap-[.3rem] p-0' role='list'>
                <li className={SYMPTOM_BAD_CLASS}>{t('grimoire.bad.freeze')}</li>
                <li className={SYMPTOM_BAD_CLASS}>{t('grimoire.bad.kick')}</li>
                <li className={SYMPTOM_BAD_CLASS}>{t('grimoire.bad.shot')}</li>
              </ul>
            </div>
            <div className='grimoire__col grimoire__col--good grid min-w-0 content-start rounded-[var(--r-md)] border border-solid border-[color:rgb(var(--accent-rgb)_/_.3)] bg-[color:rgb(var(--accent-rgb)_/_.06)] p-3 lg:content-between'>
              <p className={COLUMN_LABEL_CLASS}>
                {t('grimoire.col_good')}{' '}
                <span className='grimoire__chip rounded-full bg-[color:var(--accent)] px-[.4rem] py-[.12rem] text-[length:11px] tracking-[.06em] text-[color:var(--accent-ink)] forced-colors:border forced-colors:border-solid forced-colors:border-[color:CanvasText]' data-probe-chip=''>
                  −{delta}%
                </span>
              </p>
              <p className='grimoire__val m-0 mt-1 flex min-w-0 items-baseline gap-1 lg:m-0 lg:mt-2'>
                <b className={`${VALUE_CLASS} text-[color:var(--accent)] [text-shadow:var(--glow-text)]`} data-probe-on=''>
                  {lastGood}
                </b>
                <em className='text-[length:var(--fs-xs)] not-italic text-[color:var(--fg-dim)]'>ms</em>
              </p>
              <ul className='grimoire__sym grimoire__sym--ok m-0 mt-[.55rem] flex min-w-0 list-none flex-wrap gap-[.3rem] p-0' role='list'>
                <li className={SYMPTOM_GOOD_CLASS}>{t('grimoire.good.loss')}</li>
                <li className={SYMPTOM_GOOD_CLASS}>{t('grimoire.good.jitter')}</li>
                <li className={SYMPTOM_GOOD_CLASS}>{t('grimoire.good.fps')}</li>
              </ul>
            </div>
          </div>

          <p className='grimoire__foot m-0 min-w-0 text-[length:var(--fs-xs)] leading-normal text-[color:var(--fg-dim)] lg:[grid-area:foot]'>
            <Trans t={t} i18nKey='grimoire.foot' components={{ b: <b className='text-[color:var(--fg)]' /> }} />
          </p>
        </figure>

        <Trust />
      </div>
    </section>
  );
}
